import type { Retailer } from '@packages/clients';

import { type AllowCaseFeatures, TechlineStatusCode } from '../types';
import { caseStatusMap, DEFAULT_ALLOW_CASE_FEATURES } from './constants';

/**
 * Checks if a given primary case type indicates a CID/CVN case, which has unique handling in the UI.
 * This is determined by checking if the primary case type string exactly matches 'CID/CVN'.
 *
 * @param primaryCaseType - The primary case type string to check.
 * @param caseTypes - The array of case types associated with the case, used as a fallback if primaryCaseType is not provided.
 * @returns true if the primary case type is 'CID/CVN', false otherwise.
 */
export function isCidCvnCase(primaryCaseType: Retailer['primaryCaseType'], caseTypes: Retailer['caseTypes']): boolean {
    if (primaryCaseType) {
        return primaryCaseType === 'CID/CVN';
    }

    if (caseTypes && caseTypes.length) {
        return caseTypes.some((ct) => ct.caseType === 'CID/CVN' && ct.isPrimary);
    }
    return false;
}

/**
 *  Checks if a given Techline status code corresponds to a closed case.
 *
 * @param techlineStatusCode - The Techline status code to check.
 * @returns true if the status code indicates a closed case, false otherwise.
 */
export function isClosedCase(techlineStatusCode: TechlineStatusCode): boolean {
    return caseStatusMap.CLOSED.includes(techlineStatusCode);
}

/**
 * Checks if a given Techline status code corresponds to a draft case.
 *
 * @param techlineStatusCode - The Techline status code to check.
 * @returns true if the status code indicates a draft case, false otherwise.
 */
export function isDraftCase(techlineStatusCode: TechlineStatusCode): boolean {
    return caseStatusMap.DRAFT.includes(techlineStatusCode);
}

/**
 * Checks if a given Techline status code corresponds to an escalated case.
 * ESCALATED is a member of the SUBMITTED meta-group but is often special-cased
 * in UI logic (e.g. hiding the Close Case button).
 *
 * @param techlineStatusCode - The Techline status code to check.
 * @returns true if the status code indicates an escalated case, false otherwise.
 */
export function isEscalatedCase(techlineStatusCode: TechlineStatusCode): boolean {
    return techlineStatusCode === TechlineStatusCode.ESCALATED;
}

/**
 *  Checks if a given Techline status code corresponds to a submitted case.
 *
 * @param techlineStatusCode - The Techline status code to check.
 * @returns true if the status code indicates a submitted case, false otherwise.
 */
export function isSubmittedCase(techlineStatusCode: TechlineStatusCode): boolean {
    return caseStatusMap.SUBMITTED.includes(techlineStatusCode);
}

/**
 *
 * Checks if the primary case type indicate that certain features should be allowed or restricted.
 *
 * @param dto - The DTO containing the primary case type and case types.
 * @returns The allowed case features based on the primary case type and case types.
 */
export function resolveAllowedCaseFeatures(dto: Pick<Retailer, 'primaryCaseType' | 'caseTypes'>): AllowCaseFeatures {
    if (isCidCvnCase(dto.primaryCaseType, dto.caseTypes)) {
        return {
            allowAdditionalWorksheets: false,
            allowCloseCase: false,
            allowCustomerConcernDetails: false,
            allowDtcCodes: false,
            allowFailCodes: false,
            allowTechDescription: false,
        };
    }

    return DEFAULT_ALLOW_CASE_FEATURES;
}
